import { inject, Injectable } from '@angular/core';
import { bareKeyPath } from '../../../utils/dynamic-option-filters.util';
import { Sort } from '@angular/material/sort';
import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  debounceTime,
  defaultIfEmpty,
  distinctUntilChanged,
  forkJoin,
  isObservable,
  map,
  merge,
  Observable,
  of,
  scan,
  skip,
  startWith,
  switchMap,
  take,
  takeUntil,
  catchError,
  tap,
  shareReplay,
  finalize,
} from 'rxjs';
import { UtilityService } from '../../../services/utility.service';
import { EntriesService } from '../../../../features/admin/modules/features/entries/entries.service';
import type {
  AutoFillMapping,
  AvailabilityModuleConfig,
  Entry,
  Module,
  OptionsItem,
} from '../../../../features/admin/modules/modules.model';
import { ModulesStore } from '../../../../features/admin/modules/modules.store';
import { FiltersService } from '../../../../features/admin/modules/features/filters/filters.service';
import { FormService } from '../../../../features/admin/modules/features/form-builder/form.service';
import {
  evaluateMappingConditions,
  extractArrayIndicesFromField,
} from '../../../utils/mapping-condition-evaluator.util';
import { PlanningService } from '../../../../api/services/planning.service';
import { isEmptyOptionValue } from './option-value.util';
import { debugLog } from '../../../utils/debug-logger';
import { UserStore } from 'app/core/services/user.store';
import {
  canReadGeneratedField,
  canReadFieldRight,
  effectiveFieldRightForKeyPath,
} from '../../../../features/admin/modules/field-rights.util';
import { FormHelper } from '../../../../features/admin/modules/features/form-builder/form.util';

export interface LinkedFieldData extends OptionsItem {
  module: Module;
}

export interface DynamicOptionsConfig {
  field: FormlyFieldConfig;
  onOptionsUpdate: (options: any[]) => void;
  onFilterValueUpdate: (value: any) => void;
  /** If true, skip initial fetch and wait for user interaction */
  lazyLoad?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class DynamicOptionsService {
  private modulesStore = inject(ModulesStore);
  private entriesService = inject(EntriesService);
  private filtersService = inject(FiltersService);
  private formService = inject(FormService);
  private planningService = inject(PlanningService);
  private userStore = inject(UserStore);

  // Request deduplication cache - prevents duplicate concurrent API calls
  // Key: moduleId + filter, Value: { observable, timestamp }
  private readonly requestCache = new Map<string, { observable: Observable<any[]>; timestamp: number }>();
  private readonly CACHE_TTL_MS = 5000; // Cache for 5 seconds

  // Initialize dynamic options handling
  initializeDynamicOptions(
    config: DynamicOptionsConfig,
    destroy$: Observable<void>
  ): {
    linkedData: LinkedFieldData[];
    cleanup: () => void;
  } {
    const subscriptions: { unsubscribe: () => void }[] = [];

    // Skip observable options subscription if lazy loading - will be handled on interaction
    if (!config.lazyLoad && config.field.props.options && isObservable(config.field.props.options)) {
      let optionsSubscription = config.field.props.options;
      if (destroy$) {
        optionsSubscription = optionsSubscription.pipe(takeUntil(destroy$));
      }
      const subscription = optionsSubscription.subscribe(options => {
        config.onOptionsUpdate(options);
      });
      subscriptions.push(subscription);
      return { linkedData: [], cleanup: () => subscriptions.forEach(s => s.unsubscribe()) };
    }

    const linkedData = this.createLinkedData(config.field);
    if (!linkedData.length) {
      return { linkedData: [], cleanup: () => {} };
    }

    const dynamicSubscription = this.setupDynamicFilterSubscriptions(linkedData, config, destroy$);
    subscriptions.push(...dynamicSubscription);
    return {
      linkedData,
      cleanup: () => subscriptions.forEach(sub => sub.unsubscribe()),
    };
  }

  // Create linked data from field options
  createLinkedData(field: FormlyFieldConfig): LinkedFieldData[] {
    return (field.props?.optionsArray ?? []).map(option => ({
      ...option,
      filters: option.filters ?? [],
      module: this.modulesStore.getModuleByCode(option.moduleCode),
    }));
  }

  // Setup subscriptions for dynamic filters
  private setupDynamicFilterSubscriptions(
    linkedData: LinkedFieldData[],
    config: DynamicOptionsConfig,
    destroy$?: Observable<void>
  ) {
    const subscriptions: { unsubscribe: () => void }[] = [];

    linkedData.forEach(data => {
      data.filters?.forEach(filter => {
        if (filter.basedOn !== 'dynamic') return;

        const key = filter.fieldKeyPath.split('.').pop() as string;
        const dependentField = this.findDependentField(key, filter.fieldKeyPath, config.field);
        if (!dependentField?.formControl) return;

        const subscription = this.createDependentFieldSubscription(
          dependentField.formControl,
          filter,
          linkedData,
          config,
          destroy$
        );
        subscriptions.push(subscription);
      });
    });

    return subscriptions;
  }

  // Find the field that our options depend on
  private findDependentField(key: string, fullPath: string, field: FormlyFieldConfig): FormlyFieldConfig | undefined {
    return (
      // A form built on dotted keys ('general.company', as the interface-action popup and the bulk
      // bar are) has no nested groups to walk — match the dotted key itself, before a leaf match
      // could pick a different field that happens to be keyed 'company'. A no-op for other forms.
      UtilityService.findClosestFieldByKey(field, bareKeyPath(fullPath)) ??
      UtilityService.findClosestFieldByKey(field, key) ??
      UtilityService.findFieldByKeyValue(field, fullPath)
    );
  }

  // Create subscription for dependent field
  private createDependentFieldSubscription(
    control: FormlyFieldConfig['formControl'],
    filter: any,
    linkedData: LinkedFieldData[],
    config: DynamicOptionsConfig,
    destroy$?: Observable<void>
  ) {
    // Skip initial fetch if lazy loading is enabled
    if (!config.lazyLoad) {
      // Execute immediately for initial value
      this.handleDynamicValueChange(control.value, filter, config, linkedData).subscribe(options =>
        config.onOptionsUpdate(options)
      );
    } else {
      // For lazy loading, only update the filter value without fetching
      const dynamicValue = filter.subFieldKeyPath
        ? UtilityService.keyPathToValue(control.value, filter.subFieldKeyPath)
        : control.value;
      config.onFilterValueUpdate(dynamicValue);
    }

    // Subscribe to future changes.
    // Seed distinctUntilChanged with the current value so the first valueChanges emission
    // (from Formly model re-sync) is filtered out when it matches the initial value.
    // Without this, the first emission always passes (no previous to compare), triggering
    // an unnecessary fetchOptionsForValue that can cause false invalidOption errors.
    let valueChanges$ = control.valueChanges.pipe(
      debounceTime(300),
      startWith(control.value),
      distinctUntilChanged((a, b) => this.areValuesEqual(a, b)),
      skip(1)
    );

    // Only apply takeUntil if destroy$ is provided
    if (destroy$) {
      valueChanges$ = valueChanges$.pipe(takeUntil(destroy$));
    }

    return valueChanges$
      .pipe(
        switchMap(value => {
          return this.handleDynamicValueChange(value, filter, config, linkedData);
        })
      )
      .subscribe(options => config.onOptionsUpdate(options));
  }

  private handleDynamicValueChange(
    value: any,
    filter: any,
    config: DynamicOptionsConfig,
    linkedData: LinkedFieldData[]
  ): Observable<any[]> {
    const dynamicValue = filter.subFieldKeyPath ? UtilityService.keyPathToValue(value, filter.subFieldKeyPath) : value;

    // Update the filter value via callback
    config.onFilterValueUpdate(dynamicValue);
    if (!value && config.field.props?.hideIfOptionsEmpty) {
      return of([]);
    }

    return this.fetchOptionsForValue(dynamicValue, linkedData, '', config.field);
  }

  // Fetch options based on value
  fetchOptionsForValue(
    value: any,
    linkedData: LinkedFieldData[],
    searchTerm = '',
    field: FormlyFieldConfig,
    offset = 0,
    limit = 25
  ): Observable<any[]> {
    const modules = linkedData.map(d => d.module?.code ?? d.moduleCode).join(', ');

    return forkJoin(
      linkedData.map(data => this.fetchOptionsForModule(data, value, searchTerm, field, offset, limit))
    ).pipe(
      map(responses => this.sortOptions(this.deduplicateOptions(responses.flat()))),
      // `forkJoin([])` COMPLETES WITHOUT EMITTING, and an empty `linkedData` is reachable: a field
      // with an empty `optionsArray`, the observable-options path of `initializeDynamicOptions`, or
      // a dynamic-module field whose sibling module was cleared. (An unresolved module is NOT one
      // of them — it still yields an entry, and `fetchOptionsForModule` answers `of([])` for it.)
      // Nothing throws, so no `catchError` runs; the caller just never hears back. Every autocomplete then keeps `loading` true forever: spinner row stuck, and
      // the scroll handler bails while `loading()` is true, so the field never pages again.
      // Answering "no options" is the honest result for "there is no source to ask".
      defaultIfEmpty([] as any[])
    );
  }

  // Remove duplicate options based on value/id
  private deduplicateOptions(options: any[]): any[] {
    const seen = new Set<string>();
    return options.filter(option => {
      // Determine unique key based on value type
      let uniqueKey: string;
      if (option.value?.id) {
        // Entry type with id
        uniqueKey = String(option.value.id);
      } else if (typeof option.value === 'object' && option.value !== null) {
        // Object without id - use label as fallback
        uniqueKey = option.label ?? JSON.stringify(option.value);
      } else {
        // Primitive value (string, number, etc.)
        uniqueKey = String(option.value ?? option.label);
      }

      if (seen.has(uniqueKey)) {
        return false;
      }
      seen.add(uniqueKey);
      return true;
    });
  }

  private sortOptions(options: any[]): any[] {
    if (!options || options.length === 0) return options;

    return [...options].sort((a, b) => {
      // Unavailable options always last so the user still sees them but they
      // don't push selectable options out of view.
      const disabledA = a.disabled === true ? 1 : 0;
      const disabledB = b.disabled === true ? 1 : 0;
      if (disabledA !== disabledB) return disabledA - disabledB;
      // With grouping: sort by group first, then by label
      if (a.group != null || b.group != null) {
        const groupA = (a.group ?? '').toString().toLocaleLowerCase('nl');
        const groupB = (b.group ?? '').toString().toLocaleLowerCase('nl');
        const groupCompare = groupA.localeCompare(groupB, 'nl', { sensitivity: 'base' });
        if (groupCompare !== 0) return groupCompare;
      }
      const labelA = (a.label ?? '').toString();
      const labelB = (b.label ?? '').toString();
      return labelA.localeCompare(labelB, 'nl', { sensitivity: 'base' });
    });
  }

  /**
   * Build an OData orderBy clause from the linked field's label keyPath
   * (the field the form builder selected as "option name", e.g.
   * `entry.general.volledige-naam` for users). Returns `undefined` when
   * sorting at the backend doesn't apply — `field` value-type explodes one
   * entry into multiple options client-side, so a backend sort on the
   * label path wouldn't be meaningful.
   */
  private buildOrderByFromLinkedField(linkedData: LinkedFieldData): Sort | undefined {
    if (!linkedData.moduleLinkedField) return undefined;
    if (linkedData.moduleValueType === 'field') return undefined;
    return { active: linkedData.moduleLinkedField, direction: 'asc' };
  }

  fetchOptionsForModule(
    linkedData: LinkedFieldData,
    filterValue: any,
    searchTerm: string,
    field: FormlyFieldConfig,
    offset = 0,
    limit = 25
  ): Observable<any[]> {
    if (!linkedData.module?.id) {
      console.error('Module not found:', linkedData.moduleCode);
      return of([]);
    }

    const filters = this.buildFilters(linkedData, searchTerm, field, field.options?.formState?.entry);

    // Short-circuit: if filter contains "eq 'null'" we know there won't be any results
    if (filters.includes("eq 'null'")) {
      return of([]);
    }

    // Sort op het label-veld dat in de form builder is gekozen, zodat
    // lazy-loaded paginas een consistente volgorde aanhouden i.p.v. de
    // willekeurige backend-volgorde (waardoor scroll-pagination dezelfde
    // entries opnieuw kon tonen of er juist tussenuit kon laten vallen).
    const orderBy = this.buildOrderByFromLinkedField(linkedData);

    // Create cache key for request deduplication.
    // Include a suffix when copyFieldsOnSelect is configured so that the mapped result
    // (which includes autoFillLinkedText) is cached separately from results without it.
    const autoFillSuffix = field.props?.copyFieldsOnSelect?.length ? ':af' : '';
    const orderBySuffix = orderBy ? `:${orderBy.active} ${orderBy.direction}` : '';
    const cacheKey = `${linkedData.module.id}:${offset}:${limit}:${filters}${autoFillSuffix}${orderBySuffix}`;

    // Check if we already have a cached/pending request that's still valid
    const cached = this.requestCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL_MS) {
      return cached.observable;
    }

    // Create the request and cache it (using background worker for better UI responsiveness)
    const request$ = this.entriesService
      .getEntriesBackground({
        moduleId: linkedData.module.id,
        offset: offset,
        limit: limit,
        filter: filters,
        orderBy,
      })
      .pipe(
        switchMap(entries => {
          // Check if availability checking is enabled
          if (
            !linkedData.checkAvailability ||
            !linkedData.availabilityStartDateKeyPath ||
            !linkedData.availabilityEndDateKeyPath ||
            !linkedData.availabilityModuleConfigs ||
            linkedData.availabilityModuleConfigs.length === 0
          ) {
            // No availability checking needed, return entries as-is
            return of(entries);
          }

          // Store the availability config in variables for type safety
          const availabilityStartDateKeyPath = linkedData.availabilityStartDateKeyPath;
          const availabilityEndDateKeyPath = linkedData.availabilityEndDateKeyPath;
          const availabilityModuleConfigs = linkedData.availabilityModuleConfigs;

          const entry = field.options?.formState?.entry;
          if (!entry) {
            console.warn('No entry found in formState for availability checking');
            return of(entries);
          }

          // Extract start and end dates from the entry
          const startDate = UtilityService.keyPathToValue(entry, availabilityStartDateKeyPath);
          const endDate = UtilityService.keyPathToValue(entry, availabilityEndDateKeyPath);

          if (!startDate || !endDate) {
            console.warn('Start or end date not found for availability checking', {
              startDate,
              endDate,
              startDateKeyPath: availabilityStartDateKeyPath,
              endDateKeyPath: availabilityEndDateKeyPath,
            });
            return of(entries);
          }

          // Convert DateTime to DateOnly format (YYYY-MM-DD) for API.
          // Local-time format — `toISOString()` zou een lokale CEST-datum een
          // dag terugschuiven naar UTC.
          const formatDateOnly = (dateValue: any): string => {
            if (typeof dateValue === 'string') {
              // Extract date part from ISO string (e.g., "2025-11-19T22:13:00+01:00" -> "2025-11-19")
              return dateValue.split('T')[0];
            }
            if (dateValue instanceof Date) {
              const y = dateValue.getFullYear();
              const m = String(dateValue.getMonth() + 1).padStart(2, '0');
              const day = String(dateValue.getDate()).padStart(2, '0');
              return `${y}-${m}-${day}`;
            }
            return String(dateValue);
          };

          const fromDate = formatDateOnly(startDate);
          const toDate = formatDateOnly(endDate);

          // Build request body from module configs
          const body = availabilityModuleConfigs.map(config => ({
            entryKeyPath: config.userKeyPath,
            moduleCode: config.moduleCode,
            unavailableReasonKeyPath: this.#canReadAvailabilityReason(config.moduleCode, config.descriptionKeyPath)
              ? config.descriptionKeyPath
              : '',
            filter: config.filter || '',
          }));

          debugLog('Availability', 'request', {
            fieldKey: field.key,
            module: linkedData.module?.code ?? linkedData.moduleCode,
            fromDate,
            toDate,
            body,
            dropdownEntryIds: entries.map(e => e.id),
          });

          // Make availability check request
          return this.planningService
            .searchAvailableEntries({
              fromDate: fromDate,
              endDate: toDate,
              body,
            })
            .pipe(
              map(availableEntriesResponse => {
                debugLog('Availability', 'response', {
                  fieldKey: field.key,
                  count: availableEntriesResponse.length,
                  responseEntryIds: availableEntriesResponse.map(r => r.entryId),
                  records: availableEntriesResponse,
                });

                // Build a reason-map so options can be rendered as disabled.
                // Backend semantics differ per module: for modules with
                // `entryKeyPath` (e.g. leave-of-absence) the resolved user-id
                // ends up in `r.entryId`; for base-shifts (no entryKeyPath)
                // it ends up in `r.id` while `r.entryId` is the base-shift
                // document-id. Key by both so the dropdown match works either way.
                const unavailableReasonByEntryId = new Map<string, string>();
                for (const r of availableEntriesResponse) {
                  const reason =
                    r.moduleCode === 'base-shifts'
                      ? 'Niet beschikbaar op deze dag'
                      : this.#canRenderAvailabilityReason(availabilityModuleConfigs, r.moduleCode)
                        ? r.reason?.trim() || 'Niet beschikbaar'
                        : 'Niet beschikbaar';
                  for (const candidate of [r.entryId, r.id]) {
                    if (!candidate) continue;
                    if (!unavailableReasonByEntryId.has(candidate)) {
                      unavailableReasonByEntryId.set(candidate, reason);
                    }
                  }
                }

                const dropdownIds = new Set(entries.map(e => e.id));
                const matchedIds = [...unavailableReasonByEntryId.keys()].filter(id => dropdownIds.has(id));
                const unmatchedResponseIds = [...unavailableReasonByEntryId.keys()].filter(id => !dropdownIds.has(id));
                debugLog('Availability', 'match', {
                  fieldKey: field.key,
                  unavailableMap: Object.fromEntries(unavailableReasonByEntryId),
                  matchedCount: matchedIds.length,
                  matchedIds,
                  unmatchedResponseIds,
                  unmatchedDropdownIds: [...dropdownIds].filter(id => !unavailableReasonByEntryId.has(id)),
                });

                return { entries, unavailableReasonByEntryId };
              }),
              catchError(error => {
                console.error('Error checking availability:', error);
                // On error, return all entries available (fail open)
                return of({ entries, unavailableReasonByEntryId: new Map<string, string>() });
              })
            );
        }),
        map(result => {
          const isAvailabilityResult =
            result != null && !Array.isArray(result) && typeof result === 'object' && 'entries' in result;
          const { entries: resolvedEntries, unavailableReasonByEntryId } = isAvailabilityResult
            ? (result as { entries: Entry[]; unavailableReasonByEntryId: Map<string, string> })
            : { entries: result as Entry[], unavailableReasonByEntryId: undefined };
          debugLog('Availability', 'mapping', {
            isAvailabilityResult,
            resolvedCount: Array.isArray(resolvedEntries) ? resolvedEntries.length : 'not-array',
            hasMap: !!unavailableReasonByEntryId,
            mapSize: unavailableReasonByEntryId?.size ?? 0,
          });
          return this.mapEntriesToOptions(
            resolvedEntries,
            linkedData,
            field.props?.copyFieldsOnSelect,
            field.options?.formState?.entry as Entry | undefined,
            extractArrayIndicesFromField(field),
            unavailableReasonByEntryId
          );
        }),
        // Share the result among all subscribers and replay to late subscribers
        shareReplay({ bufferSize: 1, refCount: true }),
        // Clean up cache after TTL
        finalize(() => {
          setTimeout(() => {
            this.requestCache.delete(cacheKey);
          }, this.CACHE_TTL_MS);
        })
      );

    // Store in cache with timestamp
    this.requestCache.set(cacheKey, { observable: request$, timestamp: Date.now() });

    return request$;
  }

  buildFilters(linkedData: LinkedFieldData, searchTerm: string, field: FormlyFieldConfig, entry: Entry): string {
    const filters: string[] = [];

    if (
      (searchTerm && linkedData.moduleValueType === 'entry') ||
      (searchTerm && linkedData.moduleValueType === 'entry-small')
    ) {
      filters.push(`search('${searchTerm}')`);
    }

    // Get root form to access full live values
    const rootForm = UtilityService.getFormOfRootForm(field);
    const liveFormValues = rootForm?.formControl?.getRawValue() ?? {};

    // Merge entry with live form values for dynamic filters
    const mergedEntry = {
      ...entry,
      entry: {
        ...entry?.entry,
        ...liveFormValues,
      },
    };

    const arrayIndexPath = UtilityService.getFieldIndex(field);
    const dynamicFilters = this.filtersService.filtersToFilterString(
      linkedData.filters,
      mergedEntry,
      arrayIndexPath.length ? arrayIndexPath : undefined,
      linkedData.filterLogic ?? 'or'
    );

    if (dynamicFilters) {
      filters.push(dynamicFilters);
    }

    return filters.filter(Boolean).join(' and ');
  }

  mapEntriesToOptions(
    entries: Entry[],
    linkedData: LinkedFieldData,
    autoFillMappings?: AutoFillMapping[],
    currentEntry?: Entry,
    fieldArrayIndices: number[] = [],
    unavailableReasonByEntryId?: Map<string, string>
  ): Array<{
    label: string;
    value: any;
    selectedInput?: string;
    additionalInfo?: Array<{ label: string; value: string }>;
    autoFillLinkedText?: string;
    autoFillLinkedTextGrouped?: string;
    group?: string;
    disabled?: boolean;
    unavailableReason?: string;
    description?: string;
    recommended?: boolean;
  }> {
    if (!this.#canReadConfiguredModuleField(linkedData.module.code, linkedData.moduleLinkedField)) return [];

    let options: Array<{
      label: string;
      value: any;
      selectedInput?: string;
      additionalInfo?: Array<{ label: string; value: string }>;
      autoFillLinkedText?: string;
      autoFillLinkedTextGrouped?: string;
      group?: string;
      disabled?: boolean;
      unavailableReason?: string;
    }> = [];

    const getAutoFill = (entry: Entry) => {
      if (!autoFillMappings?.length) return {};
      return {
        autoFillLinkedText: this.resolveAutoFillLinkedText(entry, autoFillMappings, currentEntry, fieldArrayIndices),
        autoFillLinkedTextGrouped: this.resolveAutoFillLinkedTextGrouped(
          entry,
          autoFillMappings,
          currentEntry,
          fieldArrayIndices
        ),
      };
    };

    const getUnavailability = (entry: Entry) => {
      const reason = entry.id ? unavailableReasonByEntryId?.get(entry.id) : undefined;
      // Optie blijft selecteerbaar; reden komt als waarschuwings-chip naast het label.
      return reason ? { unavailableReason: reason } : {};
    };

    if (linkedData.moduleValueType === 'id') {
      options = entries
        .map(entry => {
          const label = UtilityService.keyPathToValue(entry, linkedData.moduleLinkedField);

          if (label != null) {
            return {
              label,
              value: entry.id,
              ...this.extractAdditionalInfo(entry, linkedData),
              ...this.extractGroupInfo(entry, linkedData),
              ...this.extractOptionMeta(entry, linkedData),
              ...getAutoFill(entry),
              ...getUnavailability(entry),
            };
          }
        })
        .filter(Boolean); // remove undefined items
    } else if (linkedData.moduleValueType === 'entry') {
      options = entries
        .map(entry => {
          const label = UtilityService.keyPathToValue(entry, linkedData.moduleLinkedField);

          if (label != null) {
            return {
              label,
              value: { ...entry, selectedInput: label },
              ...this.extractAdditionalInfo(entry, linkedData),
              ...this.extractGroupInfo(entry, linkedData),
              ...this.extractOptionMeta(entry, linkedData),
              ...getAutoFill(entry),
              ...getUnavailability(entry),
            };
          }
        })
        .filter(Boolean); // remove undefined items
    } else if (linkedData.moduleValueType === 'entry-small') {
      options = entries
        .map(entry => {
          const label = UtilityService.keyPathToValue(entry, linkedData.moduleLinkedField);

          if (label != null) {
            return {
              label,
              value: { selectedInput: label, id: entry.id },
              ...this.extractAdditionalInfo(entry, linkedData),
              ...this.extractGroupInfo(entry, linkedData),
              ...this.extractOptionMeta(entry, linkedData),
              ...getAutoFill(entry),
              ...getUnavailability(entry),
            };
          }
        })
        .filter(Boolean); // remove undefined items
    } else if (linkedData.moduleValueType === 'field') {
      options = entries.flatMap(entry => {
        const values = UtilityService.keyPathToValue(entry, linkedData.moduleLinkedField);
        return Array.isArray(values)
          ? values.map(value => ({
              label: value?.selectedInput ?? value,
              value: value,
              ...this.extractAdditionalInfo(entry, linkedData),
              ...this.extractGroupInfo(entry, linkedData),
              ...this.extractOptionMeta(entry, linkedData),
              ...getAutoFill(entry),
              ...getUnavailability(entry),
            }))
          : [];
      });
    }

    return this.sortOptions(options);
  }

  private extractAdditionalInfo(
    entry: Entry,
    linkedData: LinkedFieldData
  ): { additionalInfo?: Array<{ label: string; value: string }>; additionalInfoText?: string } {
    // Check for relations-based additional info
    if (linkedData.relationsInfoModuleCodes?.length) {
      const relationsTexts = linkedData.relationsInfoModuleCodes
        .map(code => this.extractRelationsInfo(entry, code))
        .filter(Boolean);
      if (relationsTexts.length > 0) {
        return { additionalInfoText: relationsTexts.join(' · ') };
      }
    }

    // Check for template-based additional info
    if (linkedData.additionalInfoTemplate) {
      const text = this.parseTemplate(linkedData.additionalInfoTemplate, entry);
      return text ? { additionalInfoText: text } : {};
    }

    // Fall back to field-based additional info
    if (!linkedData.additionalInfoFields || linkedData.additionalInfoFields.length === 0) {
      return {};
    }

    const additionalInfo = linkedData.additionalInfoFields
      .map(field => {
        if (!this.#canReadEntryField(entry, field.fieldPath, linkedData.module.code)) return null;
        const value = UtilityService.keyPathToValue(entry, field.fieldPath);
        if (value != null) {
          return {
            label: field.label ?? field.fieldPath.split('.').pop() ?? '',
            value: String(value),
          };
        }
        return null;
      })
      .filter(Boolean) as Array<{ label: string; value: string }>;

    return additionalInfo.length > 0 ? { additionalInfo } : {};
  }

  /**
   * Per-option meta for card-style radio: pulls an optional description and a
   * "recommended" flag from the linked entry using the configured keyPaths.
   */
  private extractOptionMeta(
    entry: Entry,
    linkedData: LinkedFieldData
  ): { description?: string; recommended?: boolean } {
    const meta: { description?: string; recommended?: boolean } = {};

    if (linkedData.descriptionFieldPath) {
      if (!this.#canReadEntryField(entry, linkedData.descriptionFieldPath, linkedData.module.code)) return meta;
      const value = UtilityService.keyPathToValue(entry, linkedData.descriptionFieldPath);
      if (value != null && value !== '') {
        meta.description = String(value);
      }
    }

    if (linkedData.recommendedFieldPath) {
      if (!this.#canReadEntryField(entry, linkedData.recommendedFieldPath, linkedData.module.code)) return meta;
      const value = UtilityService.keyPathToValue(entry, linkedData.recommendedFieldPath);
      if (this.isTruthyFlag(value)) {
        meta.recommended = true;
      }
    }

    return meta;
  }

  private isTruthyFlag(value: unknown): boolean {
    if (value === true) return true;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      return ['true', '1', 'ja', 'yes', 'aanbevolen'].includes(value.trim().toLowerCase());
    }
    return false;
  }

  private extractRelationsInfo(entry: Entry, relationsModuleCode: string): string | null {
    const relations = entry.relations;
    if (!relations?.length) return null;

    const targetModule = this.modulesStore.getModuleByCode(relationsModuleCode);
    if (!targetModule) return null;

    const entryId = entry.id;
    if (!entryId) return null;

    const names = relations
      .filter(r => !r.entry?.archived)
      .map(r => {
        const item1 = r.entry?.['item-1'];
        const item2 = r.entry?.['item-2'];
        if (item1?.entry?.id === entryId && item2?.moduleId === targetModule.id) {
          return this.#readableReferenceLabel(item2.entry as Entry, targetModule.code);
        }
        if (item2?.entry?.id === entryId && item1?.moduleId === targetModule.id) {
          return this.#readableReferenceLabel(item1.entry as Entry, targetModule.code);
        }
        return null;
      })
      .filter(Boolean);

    if (names.length === 0) return null;

    const moduleLabel = targetModule.singularName ?? targetModule.title ?? relationsModuleCode;
    return `${moduleLabel}: ${names.join(', ')}`;
  }

  private parseTemplate(template: string, entry: Entry): string {
    const expressions = [...template.matchAll(/\{\{([^}]+)\}\}/g)].map(match => match[1]);
    if (expressions.some(expression => !this.#canReadExpression(entry, expression))) return '';

    // Replace all {{expression}} patterns with actual values from the entry
    const result = template.replace(/\{\{([^}]+)\}\}/g, (match, expression) => {
      return this.evaluateExpression(expression.trim(), entry);
    });

    // For multi-line templates: filter out empty lines and trim whitespace
    const lines = result.split('\n');
    const nonEmptyLines = lines.filter(line => line.trim() !== '');

    // Join with newlines if there were multiple lines, otherwise return as-is
    return lines.length > 1 ? nonEmptyLines.join('\n') : result;
  }

  private evaluateExpression(expression: string, entry: Entry): string {
    // Check if expression contains ternary operator (? :)
    // Use a more sophisticated regex to handle nested quotes properly
    const ternaryMatch = this.parseTernaryExpression(expression);

    if (ternaryMatch) {
      const { condition, trueValue, falseValue } = ternaryMatch;
      const conditionResult = this.evaluateCondition(condition.trim(), entry);

      // Evaluate the appropriate branch with string concatenation support
      return conditionResult
        ? this.evaluateStringExpression(trueValue.trim(), entry)
        : this.evaluateStringExpression(falseValue.trim(), entry);
    }

    // Check for string concatenation without ternary
    if (expression.includes(' + ')) {
      return this.evaluateStringExpression(expression, entry);
    }

    // Simple field value lookup
    const value = UtilityService.keyPathToValue(entry, expression);
    return value != null ? String(value) : '';
  }

  private parseTernaryExpression(
    expression: string
  ): { condition: string; trueValue: string; falseValue: string } | null {
    // Find the ? operator (not inside quotes)
    let questionIndex = -1;
    let colonIndex = -1;
    let inQuote = false;
    let quoteChar = '';
    let depth = 0;

    for (let i = 0; i < expression.length; i++) {
      const char = expression[i];
      const prevChar = i > 0 ? expression[i - 1] : '';

      // Track quotes
      if ((char === "'" || char === '"') && prevChar !== '\\') {
        if (!inQuote) {
          inQuote = true;
          quoteChar = char;
        } else if (char === quoteChar) {
          inQuote = false;
          quoteChar = '';
        }
      }

      // Track parentheses depth
      if (!inQuote) {
        if (char === '(') depth++;
        if (char === ')') depth--;
      }

      // Find ? and : outside quotes and parentheses
      if (!inQuote && depth === 0) {
        if (char === '?' && questionIndex === -1) {
          questionIndex = i;
        } else if (char === ':' && questionIndex !== -1 && colonIndex === -1) {
          colonIndex = i;
        }
      }
    }

    if (questionIndex === -1 || colonIndex === -1) {
      return null;
    }

    return {
      condition: expression.substring(0, questionIndex),
      trueValue: expression.substring(questionIndex + 1, colonIndex),
      falseValue: expression.substring(colonIndex + 1),
    };
  }

  private evaluateStringExpression(expression: string, entry: Entry): string {
    // Handle string concatenation with + operator
    // Split by + but respect quotes
    const parts = this.splitByOperator(expression, '+');

    return parts
      .map(part => {
        const trimmed = part.trim();

        // Check if it's a quoted string
        if ((trimmed.startsWith("'") && trimmed.endsWith("'")) || (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
          return trimmed.slice(1, -1);
        }

        // Otherwise treat as field path
        const value = UtilityService.keyPathToValue(entry, trimmed);
        return value != null ? String(value) : '';
      })
      .join('');
  }

  private splitByOperator(expression: string, operator: string): string[] {
    const parts: string[] = [];
    let current = '';
    let inQuote = false;
    let quoteChar = '';

    for (let i = 0; i < expression.length; i++) {
      const char = expression[i];
      const prevChar = i > 0 ? expression[i - 1] : '';

      // Track quotes
      if ((char === "'" || char === '"') && prevChar !== '\\') {
        if (!inQuote) {
          inQuote = true;
          quoteChar = char;
        } else if (char === quoteChar) {
          inQuote = false;
          quoteChar = '';
        }
      }

      // Check for operator outside quotes
      if (!inQuote && char === operator && expression[i - 1] === ' ' && expression[i + 1] === ' ') {
        parts.push(current.trim());
        current = '';
        continue;
      }

      current += char;
    }

    if (current.trim()) {
      parts.push(current.trim());
    }

    return parts;
  }

  private evaluateCondition(condition: string, entry: Entry): boolean {
    // Check for comparison operators
    const comparisonMatch = condition.match(/^(.+?)\s*(==|!=|>=|<=|>|<)\s*(.+)$/);

    if (comparisonMatch) {
      const [, left, operator, right] = comparisonMatch;
      const leftValue = this.getValue(left.trim(), entry);
      const rightValue = this.parseValue(right.trim());

      return this.compare(leftValue, operator, rightValue);
    }

    // Simple truthy check
    const value = UtilityService.keyPathToValue(entry, condition);
    return this.isTruthy(value);
  }

  private getValue(path: string, entry: Entry): any {
    // Check if it's a quoted string
    if ((path.startsWith("'") && path.endsWith("'")) || (path.startsWith('"') && path.endsWith('"'))) {
      return path.slice(1, -1);
    }

    // Check if it's a number
    if (!isNaN(Number(path))) {
      return Number(path);
    }

    // Check if it's a boolean
    if (path === 'true') return true;
    if (path === 'false') return false;
    if (path === 'null') return null;

    // Otherwise treat as field path
    return UtilityService.keyPathToValue(entry, path);
  }

  private parseValue(value: string): string {
    // Remove quotes from string literals
    if ((value.startsWith("'") && value.endsWith("'")) || (value.startsWith('"') && value.endsWith('"'))) {
      return value.slice(1, -1);
    }
    return value;
  }

  private compare(left: any, operator: string, right: any): boolean {
    // Convert to comparable types
    const leftStr = String(left ?? '');
    const rightStr = String(right ?? '');
    const leftNum = Number(left);
    const rightNum = Number(right);

    switch (operator) {
      case '==':
        return leftStr === rightStr || (leftNum === rightNum && !isNaN(leftNum) && !isNaN(rightNum));
      case '!=':
        return leftStr !== rightStr;
      case '>':
        return !isNaN(leftNum) && !isNaN(rightNum) ? leftNum > rightNum : leftStr > rightStr;
      case '<':
        return !isNaN(leftNum) && !isNaN(rightNum) ? leftNum < rightNum : leftStr < rightStr;
      case '>=':
        return !isNaN(leftNum) && !isNaN(rightNum) ? leftNum >= rightNum : leftStr >= rightStr;
      case '<=':
        return !isNaN(leftNum) && !isNaN(rightNum) ? leftNum <= rightNum : leftStr <= rightStr;
      default:
        return false;
    }
  }

  private isTruthy(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value !== 0;
    }
    if (typeof value === 'string') {
      return value.toLowerCase() !== 'false' && value !== '0';
    }
    return true;
  }

  private extractGroupInfo(entry: Entry, linkedData: LinkedFieldData): { group?: string } {
    if (!linkedData.groupByField) {
      return {};
    }

    if (!this.#canReadEntryField(entry, linkedData.groupByField, linkedData.module.code)) return {};

    const groupValue = UtilityService.keyPathToValue(entry, linkedData.groupByField);

    if (groupValue == null) {
      return {};
    }

    // If the group value is an entry object, use its selectedInput property
    if (typeof groupValue === 'object' && groupValue.selectedInput != null) {
      const moduleCode = this.#moduleCodeOf(groupValue as Entry);
      const label = moduleCode ? this.#readableReferenceLabel(groupValue as Entry, moduleCode) : '';
      return label ? { group: label } : {};
    }

    return { group: String(groupValue) };
  }

  // Inline grouped format used for compact displays (option rows, tooltips)
  // where all sources fit on a single line. Example:
  //   "Gebruikers: Ted, Jaap, Standaard locatie: TransportHub"
  resolveAutoFillLinkedText(
    entry: Entry,
    mappings: AutoFillMapping[],
    currentEntry?: Entry,
    fieldArrayIndices: number[] = []
  ): string | null {
    const groups = this.resolveAutoFillGroups(entry, mappings, currentEntry, fieldArrayIndices);
    if (groups.length === 0) return null;
    return groups.map(g => `${g.label}: ${g.names.join(', ')}`).join(', ');
  }

  // Grouped variant used for informational displays (alerts, tooltips) where
  // each source is shown on its own line with the field/module label as prefix.
  resolveAutoFillLinkedTextGrouped(
    entry: Entry,
    mappings: AutoFillMapping[],
    currentEntry?: Entry,
    fieldArrayIndices: number[] = []
  ): string | null {
    const groups = this.resolveAutoFillGroups(entry, mappings, currentEntry, fieldArrayIndices);
    if (groups.length === 0) return null;
    return groups.map(g => `${g.label}: ${g.names.join(', ')}`).join('\n');
  }

  private resolveAutoFillGroups(
    entry: Entry,
    mappings: AutoFillMapping[],
    currentEntry?: Entry,
    fieldArrayIndices: number[] = []
  ): Array<{ label: string; names: string[] }> {
    const groups: Array<{ label: string; names: string[] }> = [];

    const pushName = (label: string, name: string) => {
      let group = groups.find(g => g.label === label);
      if (!group) {
        group = { label, names: [] };
        groups.push(group);
      }
      if (!group.names.includes(name)) group.names.push(name);
    };

    for (const mapping of mappings) {
      // Skip mappings whose conditions don't hold against the current entry,
      // so the displayed preview matches what the cascading auto-fill will actually apply.
      if (
        !evaluateMappingConditions(
          mapping.conditions,
          currentEntry,
          (c, v) => this.formService.checkCondition(c, v),
          fieldArrayIndices
        )
      ) {
        continue;
      }

      if (mapping.sourceFieldPath === 'entry.relations') {
        const relations = entry.relations;
        if (!relations?.length || !mapping.sourceRelationModuleCode) continue;

        const relationModule = this.modulesStore.getModuleByCode(mapping.sourceRelationModuleCode);
        if (!relationModule) continue;

        const groupLabel = relationModule.title ?? mapping.sourceRelationModuleCode;

        for (const relation of relations) {
          if (relation.entry?.archived) continue;
          const item1 = relation.entry?.['item-1'];
          const item2 = relation.entry?.['item-2'];

          if (item1?.entry?.id === entry.id && item2?.moduleId === relationModule.id) {
            const name = this.#readableReferenceLabel(item2.entry as Entry, relationModule.code);
            if (name) pushName(groupLabel, name);
          } else if (item2?.entry?.id === entry.id && item1?.moduleId === relationModule.id) {
            const name = this.#readableReferenceLabel(item1.entry as Entry, relationModule.code);
            if (name) pushName(groupLabel, name);
          }
        }
      } else {
        if (!this.#canReadEntryField(entry, mapping.sourceFieldPath)) continue;
        const value = UtilityService.keyPathToValue(entry, mapping.sourceFieldPath);
        const reference = value as Entry | undefined;
        const moduleCode = this.#moduleCodeOf(reference) ?? this.#linkedModuleCode(entry, mapping.sourceFieldPath);
        const selectedInput = moduleCode ? this.#readableReferenceLabel(reference, moduleCode) : '';
        if (selectedInput) {
          const groupLabel = this.resolveFieldGroupLabel(entry, mapping.sourceFieldPath);
          pushName(groupLabel, String(selectedInput));
        }
      }
    }

    return groups;
  }

  // Resolve a human-readable group label for a field-based auto-fill mapping by
  // looking up the source field's label in the entry's module form. Falls back
  // to the module title or a neutral default when the field cannot be located.
  private resolveFieldGroupLabel(entry: Entry, sourceFieldPath: string): string {
    const entryModule = entry.moduleId ? this.modulesStore.getModuleById(entry.moduleId) : undefined;
    const generatedForm = entry.moduleId ? this.modulesStore.generatedForms()?.[entry.moduleId] : undefined;

    if (generatedForm?.length) {
      const keyPath = sourceFieldPath.startsWith('entry.') ? sourceFieldPath.slice('entry.'.length) : sourceFieldPath;
      const label = this.findFieldLabelByKeyPath(generatedForm, keyPath);
      if (label) return label;
    }

    return entryModule?.title ?? 'Gekoppeld aan';
  }

  private findFieldLabelByKeyPath(fields: FormlyFieldConfig[], keyPath: string): string | null {
    for (const field of fields) {
      if (field.props?.keyPath === keyPath) {
        const label = field.props?.label;
        if (typeof label === 'string' && label.length > 0) return label;
      }
      if (field.fieldGroup) {
        const found = this.findFieldLabelByKeyPath(field.fieldGroup, keyPath);
        if (found) return found;
      }
    }
    return null;
  }

  #canReadExpression(entry: Entry, expression: string): boolean {
    const withoutStrings = expression.replace(/(['"])(?:\\.|(?!\1).)*\1/g, '');
    const keyPaths = withoutStrings.match(/\bentry(?:\.[A-Za-z0-9_-]+)+/g) ?? [];
    return keyPaths.every(keyPath => this.#canReadEntryField(entry, keyPath));
  }

  #canReadEntryField(entry: Entry | undefined, keyPath: string | undefined, moduleCode?: string): boolean {
    if (!keyPath) return false;
    const moduleId = entry?.moduleId ?? (moduleCode ? this.modulesStore.moduleCodeToId(moduleCode) : undefined);
    return canReadFieldRight(
      effectiveFieldRightForKeyPath(
        moduleId ? this.modulesStore.generatedForms()[moduleId] : undefined,
        keyPath,
        this.userStore.currentUserRoles()
      )
    );
  }

  #canReadAvailabilityReason(moduleCode: string | undefined, keyPath: string | undefined): boolean {
    return this.#canReadConfiguredModuleField(moduleCode, keyPath);
  }

  #canReadConfiguredModuleField(moduleCode: string | undefined, keyPath: string | undefined): boolean {
    if (!moduleCode || !keyPath) return false;
    const moduleId = this.modulesStore.moduleCodeToId(moduleCode);
    return (
      !!moduleId &&
      canReadGeneratedField(this.modulesStore.generatedForms()[moduleId], keyPath, this.userStore.currentUserRoles())
    );
  }

  #canRenderAvailabilityReason(configs: AvailabilityModuleConfig[], moduleCode: string | undefined): boolean {
    const matchingConfigs = configs.filter(config => config.moduleCode === moduleCode);
    return (
      matchingConfigs.length > 0 &&
      matchingConfigs.every(config => this.#canReadAvailabilityReason(config.moduleCode, config.descriptionKeyPath))
    );
  }

  #moduleCodeOf(entry: Entry | undefined): string | undefined {
    return entry?.moduleId ? this.modulesStore.getModuleById(entry.moduleId)?.code : undefined;
  }

  #linkedModuleCode(entry: Entry, keyPath: string): string | undefined {
    if (!entry.moduleId) return undefined;
    const field = FormHelper.getFieldByKeyPath(keyPath, this.modulesStore.generatedForms()[entry.moduleId]);
    return field?.props?.optionsArray?.find((option: OptionsItem) => option.optionsType === 'module')?.moduleCode;
  }

  #readableReferenceLabel(entry: Entry | undefined, moduleCode: string): string {
    if (!entry) return '';
    const module = entry.moduleId
      ? this.modulesStore.getModuleById(entry.moduleId)
      : this.modulesStore.getModuleByCode(moduleCode);
    const titleKeyPath = module?.titelKeyPath;
    if (
      !module ||
      !canReadGeneratedField(
        this.modulesStore.generatedForms()[module.id],
        titleKeyPath,
        this.userStore.currentUserRoles()
      )
    ) {
      return '';
    }
    return String(entry.selectedInput ?? UtilityService.keyPathToValue(entry, titleKeyPath) ?? '');
  }

  /**
   * Fetch a single option by its current value (for lazy loading validation).
   * This is used when a field has an initial value but we don't want to load all options.
   * Returns the single matching option or an empty array if not found.
   */
  fetchSingleOptionByValue(field: FormlyFieldConfig, currentValue: any): Observable<any[]> {
    // A `multiple` select hands over its whole array; matching that as one value finds nothing.
    if (Array.isArray(currentValue)) {
      if (!field?.props?.optionsArray) {
        const staticOptions = this.initializeStaticOptions(field) ?? [];
        return of(staticOptions.filter(opt => currentValue.some(val => this.valueMatches(opt.value, val))));
      }
      // The multi path has no `'field'` branch, so those options keep the single path below.
      if (!field.props.optionsArray.some((option: OptionsItem) => option.moduleValueType === 'field')) {
        return this.fetchMultipleOptionsByValues(field, currentValue);
      }
    }

    if (isEmptyOptionValue(currentValue)) {
      return of([]);
    }

    if (!field?.props?.optionsArray) {
      const staticOptions = this.initializeStaticOptions(field) ?? [];
      const matchingOption = staticOptions.find(opt => this.valueMatches(opt.value, currentValue));
      return of(matchingOption ? [matchingOption] : []);
    }

    const optionObservables: Observable<any[]>[] = [];

    field.props.optionsArray.forEach((option: OptionsItem) => {
      if (option.optionsType === 'manual') {
        // For manual options, filter locally
        const manualOptions = this.formService.generateManualOptions(option, undefined);
        const matchingOption = manualOptions.find(opt => this.valueMatches(opt.value, currentValue));
        optionObservables.push(of(matchingOption ? [matchingOption] : []));
      } else if (option.optionsType === 'taxonomy') {
        // For taxonomy options, filter locally
        // The include/exclude narrowing is applied here as well: an option the field
        // no longer offers does not exist for that field, so it must not resolve and
        // must not end up back in the dropdown.
        const taxonomyOptions = this.formService.generateTaxonomyOptions(
          option,
          this.modulesStore.moduleCodeMapper(),
          false,
          this.modulesStore.taxonomies()
        );
        const matchingOption = taxonomyOptions.find(opt => this.valueMatches(opt.value, currentValue));
        optionObservables.push(of(matchingOption ? [matchingOption] : []));
      } else if (option.optionsType === 'module') {
        // For module options, fetch only the specific entry by ID
        const linkedData = {
          ...option,
          module: this.modulesStore.getModuleByCode(option.moduleCode),
        };

        if (!linkedData.module?.id) {
          optionObservables.push(of([]));
          return;
        }

        // For 'field' valueType the saved value carries the id of a child field value
        // inside the parent document (e.g. an hour-rate inside a project), not the
        // parent entry's id. An `id eq '<value.id>'` shortcut therefore never matches.
        // Run the full configured query instead and let downstream matching locate
        // the option among the flattened child values.
        if (option.moduleValueType === 'field') {
          optionObservables.push(
            this.fetchOptionsForModule(linkedData, undefined, '', field).pipe(catchError(() => of([])))
          );
          return;
        }

        // Extract the ID from the current value based on moduleValueType
        const entryId = this.extractIdFromValue(currentValue, option.moduleValueType);

        if (!entryId) {
          optionObservables.push(of([]));
          return;
        }

        // Build filter to fetch only this specific entry
        const idFilter = `id eq '${entryId}'`;

        optionObservables.push(
          this.entriesService
            .getEntriesBackground({
              moduleId: linkedData.module.id,
              offset: 0,
              limit: 1,
              filter: idFilter,
            })
            .pipe(
              map(entries =>
                this.mapEntriesToOptions(
                  entries,
                  linkedData,
                  field.props?.copyFieldsOnSelect,
                  field.options?.formState?.entry as Entry | undefined,
                  extractArrayIndicesFromField(field)
                )
              ),
              catchError(() => of([]))
            )
        );
      }
    });

    if (optionObservables.length === 0) {
      return of([]);
    }

    // Don't sort when manual or taxonomy options are present - they have user-defined order
    const hasFixedOrderOptions = field.props.optionsArray.some(
      (option: OptionsItem) => option.optionsType === 'manual' || option.optionsType === 'taxonomy'
    );

    return forkJoin(optionObservables).pipe(
      map(responses => {
        const deduped = this.deduplicateOptions(responses.flat());
        return hasFixedOrderOptions ? deduped : this.sortOptions(deduped);
      })
    );
  }

  /**
   * Fetch options for multiple values (for lazy loading tags/arrays).
   * This fetches all the options matching the array of current values.
   */
  fetchMultipleOptionsByValues(field: FormlyFieldConfig, currentValues: any[]): Observable<any[]> {
    if (!currentValues || !Array.isArray(currentValues) || currentValues.length === 0 || !field?.props?.optionsArray) {
      return of([]);
    }

    const optionObservables: Observable<any[]>[] = [];

    field.props.optionsArray.forEach((option: OptionsItem) => {
      if (option.optionsType === 'manual') {
        // For manual options, filter locally
        const manualOptions = this.formService.generateManualOptions(option, undefined);
        const matchingOptions = manualOptions.filter(opt =>
          currentValues.some(val => this.valueMatches(opt.value, val))
        );
        optionObservables.push(of(matchingOptions));
      } else if (option.optionsType === 'taxonomy') {
        // For taxonomy options, filter locally
        // Same as the single-value path: narrowed-away options do not exist for this
        // field, so saved values pointing at them must not resolve.
        const taxonomyOptions = this.formService.generateTaxonomyOptions(
          option,
          this.modulesStore.moduleCodeMapper(),
          false,
          this.modulesStore.taxonomies()
        );
        const matchingOptions = taxonomyOptions.filter(opt =>
          currentValues.some(val => this.valueMatches(opt.value, val))
        );
        optionObservables.push(of(matchingOptions));
      } else if (option.optionsType === 'module') {
        // For module options, fetch entries by IDs
        const linkedData = {
          ...option,
          module: this.modulesStore.getModuleByCode(option.moduleCode),
        };

        if (!linkedData.module?.id) {
          optionObservables.push(of([]));
          return;
        }

        // Extract IDs from all current values
        const entryIds = currentValues
          .map(val => this.extractIdFromValue(val, option.moduleValueType))
          .filter((id): id is string => id !== null);

        if (entryIds.length === 0) {
          optionObservables.push(of([]));
          return;
        }

        // Build filter to fetch these specific entries: id eq 'id1' or id eq 'id2' ...
        const idFilters = entryIds.map(id => `id eq '${id}'`).join(' or ');
        const filter = `(${idFilters})`;

        optionObservables.push(
          this.entriesService
            .getEntriesBackground({
              moduleId: linkedData.module.id,
              offset: 0,
              limit: entryIds.length,
              filter: filter,
            })
            .pipe(
              map(entries =>
                this.mapEntriesToOptions(
                  entries,
                  linkedData,
                  field.props?.copyFieldsOnSelect,
                  field.options?.formState?.entry as Entry | undefined,
                  extractArrayIndicesFromField(field)
                )
              ),
              catchError(() => of([]))
            )
        );
      }
    });

    if (optionObservables.length === 0) {
      return of([]);
    }

    // Don't sort when manual or taxonomy options are present - they have user-defined order
    const hasFixedOrderOptions = field.props.optionsArray.some(
      (option: OptionsItem) => option.optionsType === 'manual' || option.optionsType === 'taxonomy'
    );

    return forkJoin(optionObservables).pipe(
      map(responses => {
        const deduped = this.deduplicateOptions(responses.flat());
        return hasFixedOrderOptions ? deduped : this.sortOptions(deduped);
      })
    );
  }

  /**
   * Extract the entry ID from a field value based on the moduleValueType
   */
  private extractIdFromValue(value: any, moduleValueType: string): string | null {
    if (!value) return null;

    switch (moduleValueType) {
      case 'id':
        // Value is the ID directly
        return typeof value === 'string' ? value : String(value);
      case 'entry':
      case 'entry-small':
        // Value is an object with id property
        return value?.id ? String(value.id) : null;
      case 'field':
        // For field type, value could be an array or single value with id
        if (Array.isArray(value)) {
          return value[0]?.id ? String(value[0].id) : null;
        }
        return value?.id ? String(value.id) : null;
      default:
        // Try to get id from object, or use value directly if string
        if (typeof value === 'object' && value?.id) {
          return String(value.id);
        }
        return typeof value === 'string' ? value : null;
    }
  }

  /**
   * Check if two values match (for finding current value in options)
   */
  private valueMatches(optionValue: any, currentValue: any): boolean {
    if (optionValue === currentValue) return true;
    if (!optionValue || !currentValue) return false;

    // Compare by ID if both have id property
    if (optionValue?.id && currentValue?.id) {
      return String(optionValue.id) === String(currentValue.id);
    }

    // Compare strings directly
    if (typeof optionValue === 'string' && typeof currentValue === 'string') {
      return optionValue === currentValue;
    }

    // Compare id to string value
    if (optionValue?.id && typeof currentValue === 'string') {
      return String(optionValue.id) === currentValue;
    }

    if (typeof optionValue === 'string' && currentValue?.id) {
      return optionValue === String(currentValue.id);
    }

    return false;
  }

  initializeOptions(field: FormlyFieldConfig, searchTerm?: string, limit = 1000): Observable<any[]> {
    if (field?.props?.optionsArray) {
      const optionObservables: Observable<any[]>[] = [];

      field.props.optionsArray.forEach((option: OptionsItem) => {
        if (option.optionsType === 'manual') {
          // Synchronous manual options
          const manualOptions = this.formService.generateManualOptions(option, undefined);
          optionObservables.push(of(manualOptions));
        } else if (option.optionsType === 'taxonomy') {
          // Synchronous taxonomy options
          const taxonomyOptions = this.formService.generateTaxonomyOptions(
            option,
            this.modulesStore.moduleCodeMapper(),
            false,
            this.modulesStore.taxonomies()
          );

          optionObservables.push(of(taxonomyOptions));
        } else if (option.optionsType === 'module') {
          // Asynchronous module options
          const linkedData = {
            ...option,
            module: this.modulesStore.getModuleByCode(option.moduleCode),
          };

          const filters = this.buildFilters(linkedData, searchTerm, field, field.options?.formState?.entry);

          // Short-circuit: if filter contains "eq 'null'" we know there won't be any results
          if (filters.includes("eq 'null'")) {
            optionObservables.push(of([]));
          } else {
            // Sort by the label-field selected in the form builder so paginated
            // fetches stay consistent across page boundaries.
            const orderBy = this.buildOrderByFromLinkedField(linkedData);

            // Use background worker for better UI responsiveness
            const moduleOptionsObservable = this.entriesService
              .getEntriesBackground({
                moduleId: linkedData.module.id,
                offset: 0,
                limit,
                filter: filters,
                orderBy,
              })
              .pipe(
                map(entries =>
                  this.mapEntriesToOptions(
                    entries,
                    linkedData,
                    field.props?.copyFieldsOnSelect,
                    field.options?.formState?.entry as Entry | undefined,
                    extractArrayIndicesFromField(field)
                  )
                )
              );

            optionObservables.push(moduleOptionsObservable);
          }
        }
      });

      // Use merge to emit options as they arrive (non-blocking)
      // This allows the form to render immediately while options load in background
      // Don't sort when manual or taxonomy options are present - they have user-defined order
      const hasFixedOrderOptions = field.props.optionsArray.some(
        (option: OptionsItem) => option.optionsType === 'manual' || option.optionsType === 'taxonomy'
      );

      return merge(...optionObservables).pipe(
        scan((acc: any[], curr: any[]) => [...acc, ...curr], []),
        map(allOptions => {
          // Remove duplicates (can occur with multiple optionsArray items)
          const deduplicatedOptions = this.deduplicateOptions(allOptions);
          const sortedOptions = hasFixedOrderOptions ? deduplicatedOptions : this.sortOptions(deduplicatedOptions);

          // Set the options on the field
          try {
            field.props.options = sortedOptions;
          } catch (error) {}

          return sortedOptions;
        })
      );
    } else {
      // If no optionsArray, return static options
      return of(this.initializeStaticOptions(field));
    }
  }

  /**
   * Compare two values for equality, handling different types:
   * - Objects with id: compare by id
   * - Arrays: deep compare elements
   * - Primitives (string, number, boolean): strict equality
   * - null/undefined: strict equality
   */
  private areValuesEqual(a: unknown, b: unknown): boolean {
    // Handle null/undefined
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;

    // Handle arrays
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((item, index) => this.areValuesEqual(item, b[index]));
    }

    // Handle objects with id property (entries, taxonomies)
    if (typeof a === 'object' && typeof b === 'object') {
      const aId = (a as { id?: unknown }).id;
      const bId = (b as { id?: unknown }).id;
      if (aId !== undefined && bId !== undefined) {
        return aId === bId;
      }
      // For objects without id, use JSON comparison as fallback
      return JSON.stringify(a) === JSON.stringify(b);
    }

    // Handle primitives (string, number, boolean)
    return a === b;
  }

  lastItem(length: number, index: number): boolean {
    return length === index + 1;
  }

  initializeStaticOptions(field) {
    if (field.props.options) {
      if (isObservable(field.props.options)) {
        let returnOptions;
        // Note: This synchronous subscription pattern only works for BehaviorSubjects
        // or observables that emit synchronously. The subscription is taken once
        // to get the current value.
        field.props.options.pipe(take(1)).subscribe(options => {
          returnOptions = options;
        });
        return returnOptions;
      } else {
        return field.props.options;
      }
    }
  }
}
